import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
} from "@mui/material";

const PopupContext = createContext(null);

export const PopupProvider = ({ children }) => {
  const [currentPopup, setCurrentPopup] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const queueRef = useRef([]);
  const confirmResolverRef = useRef(null);

  const processQueue = useCallback(() => {
    if (currentPopup || queueRef.current.length === 0) return;
    setCurrentPopup(queueRef.current.shift());
  }, [currentPopup]);

  const showPopup = useCallback((message, severity = "info", options = {}) => {
    if (!message) return;

    queueRef.current.push({
      id: `${Date.now()}-${Math.random()}`,
      message,
      severity,
      duration:
        typeof options.duration === "number" ? options.duration : 4000,
    });
    setCurrentPopup((active) => active || queueRef.current.shift());
  }, []);

  const closePopup = useCallback((_, reason) => {
    if (reason === "clickaway") return;
    setCurrentPopup(null);
  }, []);

  const resolveConfirm = useCallback((value) => {
    if (confirmResolverRef.current) {
      confirmResolverRef.current(value);
      confirmResolverRef.current = null;
    }
    setConfirmState(null);
  }, []);

  const showConfirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setConfirmState({
        message: String(message ?? ""),
        title: options.title || "Confirm Action",
        confirmText: options.confirmText || "Confirm",
        cancelText: options.cancelText || "Cancel",
        severity: options.severity || "warning",
      });
    });
  }, []);

  useEffect(() => {
    if (!currentPopup) processQueue();
  }, [currentPopup, processQueue]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const originalAlert = window.alert;
    window.alert = (message) => {
      showPopup(String(message ?? ""), "info");
    };

    return () => {
      window.alert = originalAlert;
    };
  }, [showPopup]);

  const value = useMemo(
    () => ({
      showPopup,
      showConfirm,
      showSuccess: (message, options) => showPopup(message, "success", options),
      showError: (message, options) => showPopup(message, "error", options),
      showWarning: (message, options) => showPopup(message, "warning", options),
      showInfo: (message, options) => showPopup(message, "info", options),
    }),
    [showPopup, showConfirm],
  );

  return (
    <PopupContext.Provider value={value}>
      {children}
      <Snackbar
        open={!!currentPopup}
        autoHideDuration={currentPopup?.duration ?? 4000}
        onClose={closePopup}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closePopup}
          severity={currentPopup?.severity ?? "info"}
          variant="filled"
          sx={{ width: "100%", minWidth: 320, boxShadow: 3 }}
        >
          {currentPopup?.message || ""}
        </Alert>
      </Snackbar>
      <Dialog
        open={!!confirmState}
        onClose={() => resolveConfirm(false)}
        aria-labelledby="shared-confirm-title"
        aria-describedby="shared-confirm-description"
      >
        <DialogTitle id="shared-confirm-title">
          {confirmState?.title || "Confirm Action"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="shared-confirm-description">
            {confirmState?.message || ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => resolveConfirm(false)} color="inherit">
            {confirmState?.cancelText || "Cancel"}
          </Button>
          <Button
            onClick={() => resolveConfirm(true)}
            variant="contained"
            color={confirmState?.severity === "error" ? "error" : "warning"}
          >
            {confirmState?.confirmText || "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </PopupContext.Provider>
  );
};

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error("usePopup must be used within a PopupProvider");
  }
  return context;
};
